// ──────────────────────────────────────────────
// React hooks for session services.
// Use useSyncExternalStore for tear-free reads.
// ──────────────────────────────────────────────

import { useSyncExternalStore } from 'react';

import type { SessionCompletionService } from './SessionCompletionService';
import type { SessionRecoveryService } from './SessionRecoveryService';
import type { RecoveryState, SessionCompletionState } from './types';

// ── Fallback snapshots (returned when service is null) ──

const IDLE_COMPLETION: SessionCompletionState = {
  status: 'idle',
  totalChunks: 0,
  uploadedChunks: 0,
  failedChunks: 0,
  drainElapsedSeconds: 0,
  drainTimeoutSeconds: 30,
  canGenerateNote: false,
  offlineChunksPending: 0,
};

const IDLE_RECOVERY: RecoveryState = {
  status: 'idle',
  activeRecovery: null,
  recoveredSessions: [],
  failedRecoveries: [],
};

const noop = () => () => {};

/**
 * Subscribe to SessionCompletionService state.
 * Returns a static idle state when service is null.
 */
export function useSessionCompletion(
  service: SessionCompletionService | null,
): SessionCompletionState {
  return useSyncExternalStore(
    service ? (cb) => service.subscribe(cb) : noop,
    service ? () => service.getSnapshot() : () => IDLE_COMPLETION,
  );
}

/**
 * Subscribe to SessionRecoveryService state.
 * Returns a static idle state when service is null.
 */
export function useSessionRecovery(
  service: SessionRecoveryService | null,
): RecoveryState {
  return useSyncExternalStore(
    service ? (cb) => service.subscribe(cb) : noop,
    service ? () => service.getSnapshot() : () => IDLE_RECOVERY,
  );
}
